import { generateDateMonthsPrevious } from 'applicationTable/applicationUtils';
import { serializeQueryString } from 'utils/common/common';

const INTRODUCER_PORTAL_NAMESPACE = '/api/introducer_portal';
const LENDING_API_NAMESPACE = '/api/lending/edge';
export const DEFAULT_INTRODUCER_ID = '40023747-5bb5-49a3-bae1-ed23cd1562f3';
export const DEFAULT_INTRODUCER_ACCOUNT_ID =
  '3ec465ff-ae4e-4a99-bdb5-8ae03673c82f';
export const DEFAULT_STATE_KEY = '6ffe9bcb-b1ae-4839-bd67-54bde22d02ab';
export const DEFAULT_APPLICATION_ID = '1';
export const DEFAULT_OFFER_ID = '70ed2dd1-6187-495d-ab7b-e128d0d8f8ac';
const DEFAULT_APPLICATION_MONTHS_FILTER_VALUE = 1;

type TCommonParams = {
  introducerId?: string;
  applicationId?: string;
  offerId?: string;
};

type TRepaymentSimulatorQueryParams = {
  product?: string;
  initial_principal?: number | string;
  '30_day_rate'?: string | number;
  loan_duration_months?: number;
  funding_fee?: number | string;
  start_date?: string;
  early_final_repayment_date?: string;
};

type TGetApplicationsQueryParams = {
  search?: string;
  date_from?: string | null;
  fromPreviousMonths?: number | null;
  include_partner_channel?: boolean;
};

type TNotificationsParams = {
  page_no?: number;
  per_page?: number;
};

export type TDrawdownRepaymentQueryParams = {
  amount: number | string;
  early_final_repayment_date?: string;
  overpayment_repayment_amount?: number;
  minimum_monthly_repayment?: number;
};

type TGetLegalFormQueryParams = {
  crefo_id: string;
};

export const URLS = {
  acceptOffer: ({
    introducerId = DEFAULT_INTRODUCER_ID,
    applicationId = DEFAULT_APPLICATION_ID,
    offerId = DEFAULT_OFFER_ID,
  }: TCommonParams) =>
    `${INTRODUCER_PORTAL_NAMESPACE}/introducer/${introducerId}/application/${applicationId}/offer/${offerId}/accept/`,

  allowedWithdrawalFeeRates: ({
    introducerId = DEFAULT_INTRODUCER_ID,
    applicationId = DEFAULT_APPLICATION_ID,
    offerId = DEFAULT_OFFER_ID,
  }: TCommonParams) =>
    `${INTRODUCER_PORTAL_NAMESPACE}/introducer/${introducerId}/application/${applicationId}/offer/${offerId}/allowed_withdrawal_fee_rates/`,

  expectedCommission: ({
    introducerId = DEFAULT_INTRODUCER_ID,
    applicationId = DEFAULT_APPLICATION_ID,
    offerId = DEFAULT_OFFER_ID,
  }: TCommonParams) =>
    `${INTRODUCER_PORTAL_NAMESPACE}/introducer/${introducerId}/application/${applicationId}/offer/${offerId}/expected_commission/`,

  withdrawalFeeRate: ({
    introducerId = DEFAULT_INTRODUCER_ID,
    applicationId = DEFAULT_APPLICATION_ID,
    offerId = DEFAULT_OFFER_ID,
  }: TCommonParams) =>
    `${INTRODUCER_PORTAL_NAMESPACE}/introducer/${introducerId}/application/${applicationId}/offer/${offerId}/withdrawal_fee_rate/`,

  rejectOfferReasons: ({
    introducerId = DEFAULT_INTRODUCER_ID,
    applicationId = DEFAULT_APPLICATION_ID,
  }: Omit<TCommonParams, 'offerId'>) =>
    `${INTRODUCER_PORTAL_NAMESPACE}/introducer/${introducerId}/application/${applicationId}/reject_offer_reasons/`,

  fundingRequirements: ({
    introducerId = DEFAULT_INTRODUCER_ID,
    applicationId = DEFAULT_APPLICATION_ID,
    offerId = DEFAULT_OFFER_ID,
  }: TCommonParams) =>
    `${INTRODUCER_PORTAL_NAMESPACE}/introducer/${introducerId}/application/${applicationId}/offer/${offerId}/funding_requirements/`,

  customerContactUpdates: ({
    introducerId = DEFAULT_INTRODUCER_ID,
    applicationId = DEFAULT_APPLICATION_ID,
  }: TCommonParams) =>
    `${INTRODUCER_PORTAL_NAMESPACE}/introducer/${introducerId}/application/${applicationId}/customer_contact_updates/`,

  repaymentSimulator: ({ query }: { query?: TRepaymentSimulatorQueryParams }) =>
    `${INTRODUCER_PORTAL_NAMESPACE}/repayment_simulator/${
      query ? `?${serializeQueryString(query)}` : ''
    }`,

  postIntroducerAccount: () =>
    `${INTRODUCER_PORTAL_NAMESPACE}/introducer_account/`,

  patchIntroducerAccount: ({
    introducerAccountId = DEFAULT_INTRODUCER_ACCOUNT_ID,
  }: {
    introducerAccountId?: string;
  } = {}) =>
    `${INTRODUCER_PORTAL_NAMESPACE}/introducer_account/${introducerAccountId}/`,

  postIntroducer: () => `${INTRODUCER_PORTAL_NAMESPACE}/introducer/`,

  postIntroducerDocuments: ({ introducerId = DEFAULT_INTRODUCER_ID }) =>
    `${INTRODUCER_PORTAL_NAMESPACE}/introducer/${introducerId}/document/`,

  postRefreshApplication: ({
    introducerId = DEFAULT_INTRODUCER_ID,
    applicationId = DEFAULT_APPLICATION_ID,
  }: TCommonParams = {}) =>
    `${INTRODUCER_PORTAL_NAMESPACE}/introducer/${introducerId}/application/${applicationId}/refresh/`,

  applications: ({
    introducerId = DEFAULT_INTRODUCER_ID,
    query = {},
  }: {
    introducerId?: string;
    query?: TGetApplicationsQueryParams;
  }) => {
    const {
      search,
      fromPreviousMonths = DEFAULT_APPLICATION_MONTHS_FILTER_VALUE,
      date_from,
      include_partner_channel,
    } = query;

    let queryData = null;

    if (search) {
      queryData = { search };
    } else if ((date_from || fromPreviousMonths) && date_from !== null) {
      queryData = {
        date_from:
          date_from || generateDateMonthsPrevious(fromPreviousMonths as number),
      };
    }

    if (include_partner_channel) {
      queryData = {
        ...queryData,
        include_partner_channel: include_partner_channel,
      };
    }

    return `${LENDING_API_NAMESPACE}/introducer/${introducerId}/applications/${
      queryData ? `?${serializeQueryString(queryData)}` : ''
    }`;
  },

  notifications: ({
    introducerId = DEFAULT_INTRODUCER_ID,
    query,
  }: {
    introducerId?: string;
    query?: TNotificationsParams;
  }) =>
    `${INTRODUCER_PORTAL_NAMESPACE}/introducer/${introducerId}/notification/${
      query ? `?${serializeQueryString(query)}` : ''
    }`,

  patchNotifications: ({
    introducerId = DEFAULT_INTRODUCER_ID,
    applicationId = DEFAULT_APPLICATION_ID,
    notificationId,
  }: Omit<TCommonParams, 'offerId'> & { notificationId: string }) =>
    `${INTRODUCER_PORTAL_NAMESPACE}/introducer/${introducerId}/application/${applicationId}/notification/${notificationId}`,

  patchAllNotifications: ({
    introducerId = DEFAULT_INTRODUCER_ID,
  }: Pick<TCommonParams, 'introducerId'>) =>
    `${INTRODUCER_PORTAL_NAMESPACE}/introducer/${introducerId}/notification`,

  patchSubscriptions: ({
    introducerId = DEFAULT_INTRODUCER_ID,
  }: {
    introducerId?: string;
  }) =>
    `${INTRODUCER_PORTAL_NAMESPACE}/introducer/${introducerId}/notification/subscription/`,

  getSubscriptions: ({
    introducerId = DEFAULT_INTRODUCER_ID,
  }: {
    introducerId?: string;
  }) =>
    `${INTRODUCER_PORTAL_NAMESPACE}/introducer/${introducerId}/notification/subscription/`,

  getState: () => `${LENDING_API_NAMESPACE}/state/`,

  patchState: ({
    introducerId = DEFAULT_INTRODUCER_ID,
    stateKey = DEFAULT_STATE_KEY,
  }: {
    introducerId?: string;
    stateKey?: string;
  }) =>
    `${INTRODUCER_PORTAL_NAMESPACE}/introducer/${introducerId}/state/${stateKey}/`,

  profile: () => `${LENDING_API_NAMESPACE}/profile/`,

  getOffers: ({
    introducerId = DEFAULT_INTRODUCER_ID,
    applicationId = DEFAULT_APPLICATION_ID,
  }: Omit<TCommonParams, 'offerId'>) =>
    `${INTRODUCER_PORTAL_NAMESPACE}/introducer/${introducerId}/application/${applicationId}/offers/`,

  lookupPostcode: ({ postcode }: { postcode: string }) =>
    `${LENDING_API_NAMESPACE}/postcode_lookup/?postcode=${postcode}`,

  getIntroducerApplication: ({
    introducerId = DEFAULT_INTRODUCER_ID,
    applicationId = DEFAULT_APPLICATION_ID,
  }: Omit<TCommonParams, 'offerId'>) =>
    `${INTRODUCER_PORTAL_NAMESPACE}/introducer/${introducerId}/application/${applicationId}/`,

  putApplication: ({
    introducerId = DEFAULT_INTRODUCER_ID,
    applicationId = DEFAULT_APPLICATION_ID,
  }: Omit<TCommonParams, 'offerId'>) =>
    `${INTRODUCER_PORTAL_NAMESPACE}/introducer/${introducerId}/application/${applicationId}/`,

  postApplication: ({
    stateKey = DEFAULT_STATE_KEY,
    introducerId = DEFAULT_INTRODUCER_ID,
  }: {
    introducerId?: string;
    stateKey?: string;
  }) =>
    `${INTRODUCER_PORTAL_NAMESPACE}/introducer/${introducerId}/customer/${stateKey}/application/`,

  getDeclineReason: ({
    introducerId = DEFAULT_INTRODUCER_ID,
    applicationId = DEFAULT_APPLICATION_ID,
  }: Omit<TCommonParams, 'offerId'>) =>
    `${INTRODUCER_PORTAL_NAMESPACE}/introducer/${introducerId}/application/${applicationId}/decline_reason/`,

  getDocumentUpload: ({ stateKey }: { stateKey: string }) =>
    `${LENDING_API_NAMESPACE}/document_upload/${stateKey}/`,

  getIntroducerDocumentUpload: ({
    introducerId = DEFAULT_INTRODUCER_ID,
  }: Pick<TCommonParams, 'introducerId'>) =>
    `${INTRODUCER_PORTAL_NAMESPACE}/introducer/${introducerId}/document/`,

  getLegalForm: ({ query }: { query: TGetLegalFormQueryParams }) =>
    `${LENDING_API_NAMESPACE}/legal_form/${
      query ? `?${serializeQueryString(query)}` : ''
    }`,

  companySearch: ({ companyName }: { companyName: string }) =>
    `/api/companies/search/?company_name=${companyName}`,

  companySearchDE: ({ companyName }: { companyName: string }) =>
    `${LENDING_API_NAMESPACE}/company_search/?company_name=${companyName}`,

  directorSearch: ({ companyNumber }: { companyNumber: string }) =>
    `${INTRODUCER_PORTAL_NAMESPACE}/director_search/${companyNumber}/`,

  getDrawdownRepaymentSchedule: ({
    introducerId = DEFAULT_INTRODUCER_ID,
    applicationId = DEFAULT_APPLICATION_ID,
    offerId = DEFAULT_OFFER_ID,
    query,
  }: {
    introducerId?: string;
    applicationId?: string;
    offerId?: string;
    query?: TDrawdownRepaymentQueryParams;
  }) =>
    `${INTRODUCER_PORTAL_NAMESPACE}/introducer/${introducerId}/application/${applicationId}/offer/${offerId}/draw_down_repayment_schedule/${
      query ? `?${serializeQueryString(query)}` : ''
    }`,

  introducerContext: ({
    query,
  }: {
    query?: { __impersonate: string | number };
  }) =>
    `${INTRODUCER_PORTAL_NAMESPACE}/introducer/context/${
      query ? `?${serializeQueryString(query)}` : ''
    }`,

  introducerContextWithId: ({
    introducerId = DEFAULT_INTRODUCER_ID,
  }: Pick<TCommonParams, 'introducerId'>) =>
    `${INTRODUCER_PORTAL_NAMESPACE}/introducer/${introducerId}/context/`,

  getStateFromKey: ({ stateKey = DEFAULT_STATE_KEY }: { stateKey?: string }) =>
    `${LENDING_API_NAMESPACE}/state/${stateKey}/`,

  selectOffer: ({
    offerId = DEFAULT_OFFER_ID,
  }: Pick<TCommonParams, 'offerId'>) =>
    `${LENDING_API_NAMESPACE}/offers/${offerId}/select/`,

  whatsNewContent: ({
    introducerId = DEFAULT_INTRODUCER_ID,
    query,
  }: {
    introducerId?: string;
    query?: TNotificationsParams;
  }) =>
    `${INTRODUCER_PORTAL_NAMESPACE}/introducer/${introducerId}/whats_new/${
      query ? `?${serializeQueryString(query)}` : ''
    }`,

  getHeadquartersBranches: ({ headquarters }: { headquarters: string }) =>
    `${INTRODUCER_PORTAL_NAMESPACE}/introducer/branches?headquarters=${headquarters}`,

  stateContext: ({ stateKey = DEFAULT_STATE_KEY }: { stateKey?: string }) =>
    `${LENDING_API_NAMESPACE}/context/${stateKey}/`,

  onlineAccounts: ({ stateKey = DEFAULT_STATE_KEY }: { stateKey?: string }) =>
    `${LENDING_API_NAMESPACE}/online_account/${stateKey}/`,

  reintroductionSearch: ({
    companyNumber,
    introducerId = DEFAULT_INTRODUCER_ID,
  }: {
    companyNumber: string;
    introducerId?: string;
  }) =>
    `${INTRODUCER_PORTAL_NAMESPACE}/introducer/${introducerId}/reintroduction_search/${companyNumber}/`,
};
